/* hs-eslint ignored failing-rules */

/* eslint-disable react/prop-types */
'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedHTMLMessage';
import PortalIdParser from 'PortalIdParser';
import classNames from 'classnames';
import { formatPercentage } from 'AdsUI/lib/Calculations';
import { ZeroStateData, RoiTypes, KBArticles } from 'AdsUI/lib/Constants';
import ReportRecord from 'AdsUI/records/reporting/ReportRecord';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UILink from 'UIComponents/link/UILink';
import UILock from 'ui-addon-upgrades/icons/UILock';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import { isValidRoi } from 'AdsUI/selectors/AccountsSelectors';
import { hasAccessToRoiReporting } from 'AdsUI/selectors/AuthSelectors';
import withScreen from 'AdsUI/lib/withScreen';
import FormattedCurrency from 'I18n/components/FormattedCurrency';
import Small from 'UIComponents/elements/Small';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import KnowledgeBaseLink from 'AdsUI/components/KnowledgeBaseLink';
import GoToCurrencySettings from './GoToCurrencySettings';
import AdSpendCurrencyChoiceLink from './AdSpendCurrencyChoiceLink';
const SETTINGS_URL = `/settings/${PortalIdParser.get()}/marketing/ads/roiCalculation`;

const ROILabel = ({
  suppressTooltip
}) => /*#__PURE__*/_jsx("span", {
  className: "roi-spend-label m-right-2",
  children: /*#__PURE__*/_jsx(UITooltip, {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "campaigns.roiTooltip"
    }),
    disabled: suppressTooltip,
    placement: "left",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "roi"
    })
  })
});

const SettingsLink = ({
  children
}) => /*#__PURE__*/_jsx(UILink, {
  href: SETTINGS_URL,
  className: "clickable-roi",
  external: false,
  children: children
});

const LearnMoreLink = () => /*#__PURE__*/_jsx(KnowledgeBaseLink, {
  kbArticle: KBArticles.CONFIGURE_ROI_CALCULATION,
  context: "roi-tooltip"
});

const Explanation = (_ref) => {
  let {
    children
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ["children"]);

  return /*#__PURE__*/_jsx("div", Object.assign({}, props, {
    children: /*#__PURE__*/_jsx(Small, {
      children: children
    })
  }));
};

const ROITableRow = ({
  label,
  children
}) => /*#__PURE__*/_jsxs("div", {
  className: "justify-between m-bottom-2",
  children: [/*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx("strong", {
      children: label
    })
  }), /*#__PURE__*/_jsx("div", {
    children: children
  })]
});

const FormattedROI = ({
  roi,
  addClasses
}) => {
  const classes = classNames('roi-value', addClasses, roi > 0 && "success", roi < 0 && "unsuccessful");
  return /*#__PURE__*/_jsx("span", {
    className: classes,
    children: formatPercentage(roi * 100)
  });
};

export const ROITooltipBody = ({
  data: {
    roi,
    roiType,
    roiRevenue,
    roiCost,
    roiConverted
  }
}) => /*#__PURE__*/_jsxs("div", {
  style: {
    fontSize: 14
  },
  children: [/*#__PURE__*/_jsx("p", {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `roiValue.tooltipBody.${roiType}.header`
    })
  }), roiConverted && /*#__PURE__*/_jsxs("p", {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "roiValue.tooltipBody.roiConverted"
    }), ' ', /*#__PURE__*/_jsx(GoToCurrencySettings, {})]
  }), /*#__PURE__*/_jsx(ROITableRow, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `roiValue.tooltipBody.${roiType}.revenue`
    }),
    children: /*#__PURE__*/_jsx(FormattedCurrency, {
      value: roiRevenue.amount,
      currencyCode: roiRevenue.currencyCode
    })
  }), roiType === RoiTypes.LEAD_COST_ROI && /*#__PURE__*/_jsx(Explanation, {
    className: "m-bottom-2",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "roiValue.tooltipBody.LEAD_COST_ROI.revenueExplain"
    })
  }), /*#__PURE__*/_jsx(ROITableRow, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "roiValue.tooltipBody.spend"
    }),
    children: /*#__PURE__*/_jsx(FormattedCurrency, {
      value: roiCost.amount,
      currencyCode: roiCost.currencyCode
    })
  }), /*#__PURE__*/_jsx(ROITableRow, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "roiValue.tooltipBody.roi"
    }),
    children: /*#__PURE__*/_jsx(FormattedROI, {
      roi: roi
    })
  }), /*#__PURE__*/_jsx(Explanation, {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "roiValue.tooltipBody.roiExplain"
    })
  }), /*#__PURE__*/_jsxs("div", {
    className: "m-top-3",
    children: [/*#__PURE__*/_jsx("span", {
      className: "m-right-4",
      children: /*#__PURE__*/_jsx(LearnMoreLink, {})
    }), /*#__PURE__*/_jsx(SettingsLink, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "roiValue.tooltipBody.goToSettings"
      })
    })]
  })]
});

const ConditionalTooltip = ({
  title,
  tooltipProps,
  tooltipPlacement,
  showHelpIcon,
  hideTooltip,
  children
}) => hideTooltip ? children : showHelpIcon ? /*#__PURE__*/_jsx(UIHelpIcon, {
  title: title,
  tooltipProps: tooltipProps,
  tooltipPlacement: tooltipPlacement,
  children: children
}) : /*#__PURE__*/_jsx(UITooltip, Object.assign({
  title: title,
  placement: tooltipPlacement
}, tooltipProps, {
  children: /*#__PURE__*/_jsx("span", {
    children: children
  })
}));

const ROITooltipAndLink = ({
  data,
  addClasses,
  showHelpIcon,
  hideTooltip
}) => {
  return /*#__PURE__*/_jsx(ConditionalTooltip, {
    showHelpIcon: showHelpIcon,
    title: /*#__PURE__*/_jsx(ROITooltipBody, {
      data: data
    }),
    hideTooltip: hideTooltip,
    tooltipProps: {
      use: 'longform',
      placement: 'bottom left',
      maxWidth: 400
    },
    tooltipPlacement: "bottom left",
    children: /*#__PURE__*/_jsx(FormattedROI, {
      roi: data.roi,
      addClasses: addClasses
    })
  });
};

const ZeroStateROI = ({
  addClasses,
  children
}) => /*#__PURE__*/_jsx("span", {
  className: classNames('roi-value', addClasses),
  children: children || ZeroStateData
});

const CalculateLink = ({
  addClasses
}) => /*#__PURE__*/_jsx("span", {
  className: classNames("roi-value roi-calculate-link-container", addClasses),
  children: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "calculate"
  })
});

const LabeledROI = ({
  children
}) => /*#__PURE__*/_jsxs("span", {
  className: "roi-spend-with-label",
  children: [/*#__PURE__*/_jsx(ROILabel, {}), children]
});

const UnlabeledROI = ({
  children
}) => children;

const CalculatePQL = ({
  addClasses
}) => {
  const {
    openUpgradeModal
  } = useUpgradeModal('ads-calculate-roi');
  return /*#__PURE__*/_jsx("span", {
    className: classNames("roi-value roi-calculate-link-container", addClasses),
    children: /*#__PURE__*/_jsxs(UILink, {
      onClick: openUpgradeModal,
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "calculate"
      }), /*#__PURE__*/_jsx("div", {
        className: "m-left-1 display-inline-block",
        children: /*#__PURE__*/_jsx(UILock, {})
      })]
    })
  });
};

class ROIValue extends PureComponent {
  renderBody() {
    const {
      dontShowCalculate,
      addClasses,
      data,
      isRoiValid,
      showHelpIcon = true,
      hideTooltip = false
    } = this.props;

    if (!isRoiValid) {
      if (dontShowCalculate) {
        return /*#__PURE__*/_jsx(ZeroStateROI, {
          addClasses: addClasses
        });
      } else {
        return /*#__PURE__*/_jsx(CalculateLink, {
          addClasses: addClasses
        });
      }
    } else if (!data.roiConverted && (!data.cost || data.cost.currencyCode !== data.revenue.currencyCode || !data.cost.currencyCode || !data.revenue.currencyCode)) {
      return /*#__PURE__*/_jsx(ZeroStateROI, {
        addClasses: addClasses,
        children: /*#__PURE__*/_jsx(UITooltip, {
          use: "longform",
          headingText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "roiWarning.heading"
          }),
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "roiWarning.title"
          }),
          placement: "left top",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "notApplicableAbbreviation"
          })
        })
      });
    } else if (data.roi === 'Infinity') {
      return /*#__PURE__*/_jsxs(ZeroStateROI, {
        addClasses: addClasses,
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "notApplicableAbbreviation"
        }), ' ']
      });
    } else if (data.roi !== null) {
      return /*#__PURE__*/_jsx(ROITooltipAndLink, {
        data: data,
        addClasses: addClasses,
        showHelpIcon: showHelpIcon,
        hideTooltip: hideTooltip
      });
    } else {
      return /*#__PURE__*/_jsx(ZeroStateROI, {
        addClasses: addClasses
      });
    }
  }

  render() {
    const {
      withLabel,
      accessToRoiReporting,
      addClasses
    } = this.props;
    const ROIWrapper = withLabel ? LabeledROI : UnlabeledROI;
    return /*#__PURE__*/_jsx(ROIWrapper, {
      children: accessToRoiReporting ? /*#__PURE__*/_jsx(SettingsLink, {
        children: this.renderBody()
      }) : /*#__PURE__*/_jsx(CalculatePQL, {
        addClasses: addClasses
      })
    });
  }

}

ROIValue.propTypes = {
  data: PropTypes.instanceOf(ReportRecord),
  dontShowCalculate: PropTypes.bool,
  withLabel: PropTypes.bool,
  showHelpIcon: PropTypes.bool,
  hideTooltip: PropTypes.bool
};

const mapStateToProps = state => ({
  isRoiValid: isValidRoi(state),
  accessToRoiReporting: hasAccessToRoiReporting(state)
});

export default connect(mapStateToProps)(withScreen(ROIValue));

const ROITooltipWrapper = ({
  isRoiValid,
  accessToRoiReporting,
  data,
  children
}) => {
  if (data && !data.roiConverted && (!data.cost || data.cost.currencyCode !== data.revenue.currencyCode || !data.cost.currencyCode || !data.revenue.currencyCode)) {
    return /*#__PURE__*/_jsx(UIHelpIcon, {
      title: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "campaigns.multiCurrencyTooltip.wellsSpendRoi.roi"
          })
        }), /*#__PURE__*/_jsx(AdSpendCurrencyChoiceLink, {
          messageJsx: "campaigns.multiCurrencyTooltip.wellsSpendRoi.roiText_jsx"
        })]
      }),
      children: children
    });
  }

  if (!isRoiValid || !accessToRoiReporting || !data || data.cost.currencyCode !== data.revenue.currencyCode && !data.roiConverted || data.roi === 'Infinity' || data.roi === null) {
    return children;
  }

  return /*#__PURE__*/_jsx(UIHelpIcon, {
    title: /*#__PURE__*/_jsx(ROITooltipBody, {
      data: data
    }),
    tooltipProps: {
      use: 'longform',
      maxWidth: 400
    },
    tooltipPlacement: "bottom left",
    children: children
  });
};

export const ROITooltip = connect(mapStateToProps)(ROITooltipWrapper);